import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, Check, Copy, Mail, MapPin, Clock, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import SectionHeader from "../components/common/SectionHeader";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [sending, setSending] = useState(false);

  const emailAddress = "firzailmidja@gmail.com";

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(emailAddress);
      setCopiedEmail(true);
      toast.success("Email address copied to clipboard.");
      setTimeout(() => setCopiedEmail(false), 2200);
    } catch {
      toast.error("Failed to copy email.");
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.email || !form.message) {
      toast.error("Please fill in both email and message fields.");
      return;
    }

    setSending(true);

    // Format mailto URI
    const subject = encodeURIComponent(form.subject || `Inquiry from ${form.name || "Portfolio Visitor"}`);
    const body = encodeURIComponent(
      `Name: ${form.name || "Anonymous"}\nEmail: ${form.email}\n\nMessage:\n${form.message}\n\n---\nSent via fmi.dev contact form`
    );
    const mailtoUrl = `mailto:${emailAddress}?subject=${subject}&body=${body}`;

    // Also copy drafted message to clipboard so user doesn't lose text
    navigator.clipboard.writeText(
      `To: ${emailAddress}\nSubject: ${form.subject || "Portfolio Inquiry"}\nFrom: ${form.name} (${form.email})\n\n${form.message}`
    ).catch(() => {});

    toast.success("Opening your email client... (Message text copied to clipboard as backup)");

    setTimeout(() => {
      window.location.href = mailtoUrl;
      setSending(false);
    }, 600);
  };

  return (
    <section className="pt-36 sm:pt-40 pb-28 px-6 sm:px-8 lg:px-12">
      <div className="max-w-[1200px] mx-auto">
        <SectionHeader
          eyebrow="Direct Channel"
          title="Let's"
          highlight="connect."
          meta="Direct inquiries & research"
        />

        <div className="mt-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
          
          {/* Channel Overview Column */}
          <div className="lg:col-span-5 space-y-8">
            <p className="text-foreground/80 text-lg leading-relaxed">
              Open for full-stack engineering contracts, applied AI architecture consulting, and academic informatics research collaborations.
            </p>

            {/* Direct Email Card */}
            <div className="p-6 rounded-xl border border-border/70 bg-card/40 backdrop-blur-sm space-y-3">
              <span className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-primary block">
                Primary Direct Channel
              </span>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <a
                  href={`mailto:${emailAddress}`}
                  className="font-mono text-[13px] text-foreground hover:text-primary transition-colors flex items-center gap-2"
                >
                  <Mail className="h-4 w-4 text-primary" /> {emailAddress}
                </a>
                <button
                  type="button"
                  onClick={handleCopyEmail}
                  className="inline-flex items-center gap-1.5 font-mono text-[10.5px] tracking-wider uppercase px-2.5 py-1 rounded-md border border-border/70 hover:border-primary/60 hover:bg-secondary/40 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {copiedEmail ? (
                    <>
                      <Check className="h-3 w-3 text-emerald-400" />
                      <span className="text-emerald-400">Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Availability Matrix */}
            <ul className="pt-6 border-t border-border/60 space-y-4 font-mono text-[11.5px] tracking-[0.16em] uppercase">
              <li className="flex justify-between items-center text-muted-foreground">
                <span className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-primary" /> Location</span>
                <span className="text-foreground font-medium">Indonesia · GMT+7</span>
              </li>
              <li className="flex justify-between items-center text-muted-foreground">
                <span className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-primary" /> Typical Response</span>
                <span className="text-foreground font-medium">&lt; 24–48 hours</span>
              </li>
              <li className="flex justify-between items-center text-muted-foreground">
                <span>Current Status</span>
                <span className="text-emerald-400 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Open for Select Projects
                </span>
              </li>
            </ul>
          </div>

          {/* Direct Message Form Column */}
          <div className="lg:col-span-7">
            <form onSubmit={onSubmit} className="p-7 sm:p-8 rounded-2xl border border-border/70 bg-card/30 backdrop-blur-sm grid gap-6">
              <div className="border-b border-border/60 pb-4">
                <h2 className="text-xl font-medium tracking-tight text-foreground">Compose Message</h2>
                <p className="font-mono text-[11px] text-muted-foreground mt-1">
                  Dispatches via your default mail application with clipboard backup.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field
                  label="Your Name"
                  id="contact-name"
                  placeholder="e.g. Jane Doe"
                  value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })}
                />
                <Field
                  label="Email Address *"
                  id="contact-email"
                  type="email"
                  required
                  placeholder="name@organization.com"
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                />
              </div>

              <Field
                label="Topic / Subject"
                id="contact-subject"
                placeholder="Project consultation, architecture review, etc."
                value={form.subject}
                onChange={(v) => setForm({ ...form, subject: v })}
              />

              <div>
                <label htmlFor="contact-message" className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">
                  Message Details *
                </label>
                <textarea
                  id="contact-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Outline your project scope, timeline, and questions..."
                  className="w-full rounded-xl border border-border/70 bg-secondary/20 p-4 text-[14.5px] text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors resize-none leading-relaxed"
                />
              </div>

              <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center justify-center gap-2.5 px-7 h-12 rounded-full bg-primary text-primary-foreground font-mono text-[11.5px] tracking-[0.18em] uppercase hover:bg-primary/90 transition-colors disabled:opacity-70 focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <Send className="h-3.5 w-3.5" />
                  {sending ? "Preparing Dispatch..." : "Send Message"}
                </motion.button>

                <span className="font-mono text-[10px] text-muted-foreground">
                  Encrypted TLS transport via direct mail protocol
                </span>
              </div>
            </form>
          </div>

        </div>
      </div>
    </section>
  );
}

function Field({ label, id, value, onChange, placeholder, type = "text", required = false }) {
  return (
    <div>
      <label htmlFor={id} className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">
        {label}
      </label>
      <input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-border/70 bg-secondary/20 px-4 py-2.5 text-[14px] text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
      />
    </div>
  );
}
